import React from 'react';
import { ResultComponentProps } from '../common/types';
import BaseResultDisplay from '../common/BaseResultDisplay';
import D3PhylogeneticTree from './D3PhylogeneticTree';
import { 
  Typography, 
  Space, 
  Row, 
  Col, 
  Button, 
  Switch, 
  Alert 
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

const { Text } = Typography;

// A sample Newick string for debugging purposes
const SAMPLE_NEWICK = '((A:0.1,B:0.2):0.3,(C:0.4,D:0.5):0.6);';

// 示例分类数据 - 实际应用中应从后端获取
const SAMPLE_TAXONOMY_DATA: Record<string, string> = {
  'A': 'Eukaryota',
  'B': 'Rhodophyta',
  'C': 'Viridiplantae',
  'D': 'Metazoa'
};

const PhylogeneticTreeResult: React.FC<ResultComponentProps> = ({
  data,
  title,
  onDeleteResult,
  multiResults,
}) => {
  const [useTestData, setUseTestData] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [preferPhyloXml, setPreferPhyloXml] = React.useState(true);
  
  const hasNewick = Boolean(data?.newick_string || data?.newick_tree);
  const hasPhyloXml = Boolean(data?.phyloxml_tree && typeof data.phyloxml_tree === 'string');
  
  // 根据优先级选择数据源
  const currentNewick = useTestData 
    ? SAMPLE_NEWICK 
    : (!preferPhyloXml || !hasPhyloXml) 
      ? (data?.newick_string || data?.newick_tree || '') 
      : '';
      
  const phyloXmlTree = useTestData 
    ? '' 
    : (preferPhyloXml && hasPhyloXml) 
      ? data.phyloxml_tree as string 
      : '';
  
  // 处理分类数据
  const [taxonomyData, setTaxonomyData] = React.useState<Record<string, string>>({});
  
  React.useEffect(() => {
    // 从后端数据中提取分类信息
    if (data?.taxonomy_data && typeof data.taxonomy_data === 'object') {
      setTaxonomyData(data.taxonomy_data);
    } else if (useTestData) {
      // 使用示例分类数据
      setTaxonomyData(SAMPLE_TAXONOMY_DATA);
    } else {
      // 尝试从其他字段提取分类信息
      const extractedTaxonomy: Record<string, string> = {};
      
      // 如果有序列注释，尝试从中提取分类信息
      if (data?.sequence_annotations && Array.isArray(data.sequence_annotations)) {
        data.sequence_annotations.forEach((annotation: any) => {
          if (annotation.id && annotation.taxonomy) {
            extractedTaxonomy[annotation.id] = annotation.taxonomy;
          }
        });
      }
      
      // 如果有序列元数据，尝试从中提取分类信息
      if (data?.sequences_metadata && typeof data.sequences_metadata === 'object') {
        Object.entries(data.sequences_metadata).forEach(([id, metadata]: [string, any]) => {
          if (metadata.taxonomy) {
            extractedTaxonomy[id] = metadata.taxonomy;
          }
        });
      }
      
      setTaxonomyData(extractedTaxonomy);
    }
  }, [data, useTestData]);
  
  // 下载处理函数
  const handleDownload = (fileType: 'newick' | 'phyloxml') => {
    if (!currentNewick && fileType === 'newick') return;
    if (!phyloXmlTree && fileType === 'phyloxml') return;
    
    let content = '';
    let filename = '';
    
    if (fileType === 'newick' && currentNewick) {
      content = currentNewick;
      filename = 'phylogenetic_tree.nwk';
    } else if (fileType === 'phyloxml' && phyloXmlTree) {
      content = phyloXmlTree;
      filename = 'phylogenetic_tree.xml';
    }
    
    const blob = new Blob([content], { type: 'text/plain' });
    const downloadUrl = URL.createObjectURL(blob);
    
    // 执行下载
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // 释放URL对象
    setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);
  };
  
  if (!data || (!hasNewick && !hasPhyloXml)) {
    return (
      <BaseResultDisplay
        title={title || "进化树分析"}
        data={data}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={false}
        showDistribution={false}
      >
        <Alert message="未找到进化树数据" type="error" showIcon />
      </BaseResultDisplay>
    );
  }
  
  return (
    <BaseResultDisplay
      title={title || "进化树分析"}
      data={data}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      showRawData={false}
      showDistribution={false}
    >
      <Space direction="vertical" style={{ width: '100%', marginBottom: '16px' }}>
        <Row gutter={16} align="middle">
          <Col>
            <Button onClick={() => setUseTestData(!useTestData)} type="default">
              {useTestData ? '使用原始数据' : '使用测试数据'}
            </Button>
          </Col>
          {hasNewick && hasPhyloXml && !useTestData && (
            <Col>
              <Space>
                <Text>优先使用PhyloXML:</Text>
                <Switch 
                  checked={preferPhyloXml} 
                  onChange={setPreferPhyloXml} 
                  checkedChildren="PhyloXML" 
                  unCheckedChildren="Newick" 
                />
              </Space>
            </Col>
          )}
          <Col>
            <Button 
              type="primary" 
              icon={<DownloadOutlined />} 
              onClick={() => handleDownload('newick')}
              disabled={!currentNewick}
            >
              下载Newick格式
            </Button>
          </Col>
          {phyloXmlTree && (
            <Col>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={() => handleDownload('phyloxml')}
              >
                下载PhyloXML格式
              </Button>
            </Col>
          )}
        </Row>
        {error && (
          <Alert message={error} type="warning" showIcon style={{ marginTop: '10px' }} />
        )}
        <Text type="secondary">
          {preferPhyloXml && hasPhyloXml ? 
            '当前使用PhyloXML格式数据，解析更稳定，树形更标准。' : 
            '当前使用Newick格式数据。'}
          &nbsp;使用D3.js库从头实现的进化树可视化，支持多种布局方式和交互功能。
        </Text>
      </Space>

      <div style={{ border: '1px solid #f0f0f0', borderRadius: '4px', overflow: 'auto' }}>
        <D3PhylogeneticTree 
          newickString={currentNewick}
          phyloXmlString={phyloXmlTree}
          width={800}
          height={600}
          showControls={true}
        />
      </div>
    </BaseResultDisplay>
  );
};

export default PhylogeneticTreeResult;